import { useCallback } from 'react';
import { Patient } from '@medplum/fhirtypes';
import { FEATURE_FLAGS } from '@bariendo/lib/constants';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useFeatureFlagEnabled } from '@/hooks/useFeatureFlag';
import { alert } from '@/services/alert';
import { reportError } from '@/services/error-reporting';
import { useInsurerList } from '@/hooks/useInsurerList';
import { InsurerOption } from '@/components/Timeline/TimelineItems/PatientAccessCall/useInsurerOptions';
import { InsuranceEdit, InsuranceSectionState, useInsuranceSection } from './useInsuranceSection';

export interface InsuranceCard {
  /** The Insurance section flag. Off, nothing is read and neither surface draws the card. */
  enabled: boolean;
  state: InsuranceSectionState;
  /** Member ID, group and subscriber may be edited. */
  editable: boolean;
  /** The insurer may be chosen: only once this patient's insurer list is in. */
  insurerEditable: boolean;
  insurerOptions: InsurerOption[];
  /** Saves through the bot; says so and rethrows on failure, so the row stays open. */
  saveEdit: (edit: InsuranceEdit) => Promise<void>;
}

/**
 * The patient's insurance card and who may edit it, read once for the page: the Insurance section
 * and the Intake and billing panel's Insurance, Member ID and Group ID rows both draw it.
 */
export function useInsuranceCard(patient: Patient): InsuranceCard {
  const enabled = useFeatureFlagEnabled(FEATURE_FLAGS.ENABLE_INSURANCE_SECTION);
  const { state, save } = useInsuranceSection(patient, enabled);
  const { canEditInsuranceCard } = useCurrentUser().currentUserPermissions;
  const editable = enabled && canEditInsuranceCard && state.status === 'found';

  // The one insurer list (BAR-3079), Other included (BAR-3080).
  const insurerList = useInsurerList({ enabled: editable });
  // No choice is offered until the list is in, nor when it failed to load, which would offer
  // only the insurer already on file.
  const insurerEditable = editable && !insurerList.loading && !insurerList.failed;

  const saveEdit = useCallback(
    async (edit: InsuranceEdit) => {
      try {
        await save(edit);
      } catch (error) {
        // Rethrown so the row stays open with what was entered.
        reportError(error, { component: 'InsuranceSection' });
        alert.error("Couldn't save the insurance change. Please try again.");
        throw error;
      }
    },
    [save]
  );

  return { enabled, state, editable, insurerEditable, insurerOptions: insurerList.options, saveEdit };
}
